import {
  useDynamicContext,
  useDynamicModals,
  useUserWallets,
  Wallet,
} from "@dynamic-labs/sdk-react-core";
import { useEffect } from "react";
import { useAction } from "next-safe-action/hooks";
import { ensureProfileAction } from "@/actions/profile";

type ChainType = "EVM" | "SOL" | "BTC" | "ECLIPSE" | "COSMOS";

export const useLogin = () => {
  const { primaryWallet, handleLogOut, setShowAuthFlow, user, sdkHasLoaded, setSelectedTabIndex } =
    useDynamicContext();

  const { setShowLinkNewWalletModal } = useDynamicModals();
  const userWallets = useUserWallets();

  const { execute: ensureProfile } = useAction(ensureProfileAction, {
    onError(error) {
      console.error("Failed to ensure profile", error);
    },
  });

  useEffect(() => {
    if (!sdkHasLoaded) return;
    const dynamicUserId = user?.userId;
    if (!dynamicUserId) return;
    void (async () => {
      try {
        await ensureProfile({
          dynamicUserId,
        });
      } catch (error) {
        console.error("Failed to ensure profile", error);
      }
    })();
  }, [sdkHasLoaded, user?.userId, primaryWallet?.address, ensureProfile]);

  const walletsByChain = userWallets.reduce(
    (acc, wallet) => {
      if (!acc[wallet.chain]) acc[wallet.chain] = [];
      acc[wallet.chain].push(wallet);
      return acc;
    },
    {} as Record<string, Wallet[]>,
  );

  const validWallets = {
    EVM: walletsByChain.EVM?.[0],
    SOL: walletsByChain.SOL?.[0],
    BTC: walletsByChain.BTC?.[0],
    ECLIPSE: walletsByChain.ECLIPSE?.[0],
    COSMOS: walletsByChain.COSMOS?.[0],
  };

  const allWallets = {
    EVM: walletsByChain.EVM || [],
    SOL: walletsByChain.SOL || [],
    BTC: walletsByChain.BTC || [],
    ECLIPSE: walletsByChain.ECLIPSE || [],
    COSMOS: walletsByChain.COSMOS || [],
  };

  const isChainLinked = (chain: string) => userWallets.some((wallet) => wallet.chain === chain);

  const getTabIndexForChain = (chain: ChainType) => {
    switch (chain) {
      case "EVM":
        return 1;
      case "SOL":
        return 2;
      case "BTC":
        return 3;
      case "ECLIPSE":
        return 4;
      case "COSMOS":
        return 5;
      default:
        return 1;
    }
  };

  const linkNewWallet = (preferredChain?: ChainType) => {
    if (preferredChain) {
      setSelectedTabIndex(getTabIndexForChain(preferredChain));
    }
    setShowLinkNewWalletModal(true);
  };

  return {
    address: primaryWallet?.address ?? "",
    wallets: validWallets,
    allWallets,
    walletsByChain,
    logout: handleLogOut,
    login: () => setShowAuthFlow(true),
    user,
    linkNewWallet,
    isChainLinked,
    sdkHasLoaded,
    walletCount: userWallets.length,
    dynamicWallets: userWallets,
  };
};
